/**
 * A text language for describing a construction.
 *
 * ## Why this exists
 *
 * So that a drawing can be written, and therefore generated — by a teacher typing a
 * worksheet, by a script, or by a language model given the grammar below.
 *
 * The obvious alternative was to have the generator emit the save format directly. That is
 * a worse interface for the job, and the reasons are practical rather than aesthetic:
 *
 * - The save format carries entity ids, z-order, style palettes and a schema version. A
 *   generator has to get all of it right to produce anything at all, and a single wrong id
 *   yields a file that loads with half the objects silently dropped.
 * - It has no notion of a mistake. This language reports *which line* was wrong and why, so
 *   a generator — or the person reading its output — can fix that line.
 * - It is not stable. The save format may gain a version; this grammar is a promise.
 *
 * ## The grammar
 *
 * One statement per line. `#` starts a comment. Names are assigned with `=` and referred to
 * afterwards; a statement without a name still creates its object, it just cannot be built
 * on.
 *
 * ```
 * # A triangle with its circumcircle
 * A = point 0 0
 * B = point 120 0
 * C = point 40 90
 * segment A B
 * segment B C
 * segment C A
 * O = circumcenter A B C
 * circle3 A B C
 * label A "A"
 * label O "O"
 * ```
 *
 * Every construction the editor has is available, spelled the way the toolbar spells it.
 * The full list is `STATEMENTS` below — deliberately data rather than a switch, so that the
 * help text, the validator and the builder cannot drift apart.
 *
 * ## The statements that are not in that list
 *
 * `hull` builds a solid on points that are already in space; `label`, `style`, `hide`, `marks`
 * and `variation` work on an object already built rather than
 * building one; `region` and the solids build a great many at once; and `rim` and `foot` answer
 * a question in **space** and place the result. None fits a table whose every entry is one name
 * and one `EntitySource`, so they live in the parser and are described here.
 *
 * ```
 * label A "A"                # the name drawn beside an object
 * style AB hidden            # solid | hidden | faint | accent | note
 * hide O                     # keep an object out of the picture without deleting it
 * marks C extrema            # dot a curve's turning points and write each coordinate
 * variation C                # the bảng biến thiên of that curve, drawn under the figure
 * t = tangent C 2            # the tiếp tuyến of that curve where x = 2
 * region "x >= 0" "2x + y <= 4"   # the miền nghiệm of a system, shaded
 *
 * # A solid whose corners are named, which is what a problem needs to talk about it.
 * # The dot separates the two rings, exactly as `ABCD.A'B'C'D'` does on the page.
 * prism A B C D . A2 B2 C2 D2 at 0 0 size 220 210 140   # hình hộp / lăng trụ
 * pyramid S . A B C D at 0 0 size 260 210 210           # hình chóp
 * pyramid D . A B C at 0 0 size 250 230 190             # tứ diện
 * pyramid S . A B C D at 0 0 size 240 220 240 over A    # SA vuông góc với đáy
 *
 * # Round solids. `size` is a diameter and a height, because the base is a circle.
 * cone S O at 0 0 size 380 260                          # hình nón
 * cylinder O O2 at 0 0 size 300 300                     # hình trụ
 * A = rim O 300                                         # a point on the base circle
 * K = foot O S M                                        # foot of the perpendicular, in space
 * T = hull A C B2 D2                                    # khối tứ diện ACB'D' — a solid, with faces
 * K = cut p A                                           # the piece the plane leaves, containing A
 * ```
 *
 * A solid statement creates a labelled point per corner and a segment per edge, dashes what is
 * behind the shape, and names each edge after its ends — so `style AC accent` and
 * `M = midpoint A B` both work on the next line. `size` for a prism or pyramid is width, height
 * and depth; a depth near the width reads as a floor and a small one as a sliver.
 *
 * ## Why `rim` and `foot` exist rather than `project` and a guess
 *
 * Because the figure is a *projection*, and a projection keeps ratios along a line but not
 * angles, distances or perpendicularity. `project O SM` finds the foot on the page, which is a
 * different point from the foot in space — and, having no position in space, it cannot be asked
 * whether the solid covers the line drawn to it. `foot` computes in space and places the
 * answer; `rim` does the same for an angle round a base circle. Both give back a *placed*
 * point rather than a live construction, which is what everything a solid statement makes is.
 */

import { DEFAULT_GRAPH_SCALE } from '../construction';
import { NotableKind } from '../expression';
import { asEntityId } from '../model';

export interface Parameter {
  readonly name: string;
  /** `ref` names an earlier object; `number` and `string` are literals. */
  readonly kind: 'ref' | 'number' | 'string';
}

export interface StatementSpec {
  /** The word that starts the line. */
  readonly keyword: string;
  readonly parameters: readonly Parameter[];
  /** What the statement makes, for the help text. */
  readonly summary: string;
  /**
   * Builds the source. Receives resolved references and literals in declared order.
   *
   * Returns the `EntitySource` as a plain object; the caller assigns the id. Kept as data
   * here so that adding a statement is one entry rather than an edit in three places.
   */
  readonly build: (args: readonly unknown[]) => Record<string, unknown>;
}

/**
 * What a free plane names as its viewpoint before the parser has decided which one.
 *
 * A `build` sees only the statement's own arguments, and the viewpoint is a fact about the
 * *figure* — the first plane brings one and the rest share it. So the statement writes a
 * placeholder and `parse` replaces it, exactly as the plane statements have their `solid`
 * rewritten from the points they were given. Not a real id, and never reaches the document.
 */
export const PENDING_SPACE = asEntityId('__space');

const ref = (name: string): Parameter => ({ name, kind: 'ref' });
const num = (name: string): Parameter => ({ name, kind: 'number' });
const str = (name: string): Parameter => ({ name, kind: 'string' });

/**
 * Every statement, as data.
 *
 * The order is the order the help text lists them, grouped the way the toolbar groups tools —
 * a generator reads this list, and so does a human.
 */
export const STATEMENTS: readonly StatementSpec[] = [
  /* ------------------------------------------------------------------- points */
  {
    keyword: 'point',
    parameters: [num('x'), num('y')],
    summary: 'A free point at the given coordinates.',
    build: ([x, y]) => ({ op: 'free-point', position: { x, y } }),
  },
  {
    /*
     * A point at *graph* coordinates, which `point` cannot express.
     *
     * `axes` and `graph` both draw at {@link DEFAULT_GRAPH_SCALE} world units to the
     * mathematical unit, and `point` takes world units. So the two live in different
     * coordinate systems, and until this existed there was no way to say "A(1; 2)" at all.
     *
     * The failure was not subtle and it was not caught by anything. Asked for "tam giác ABC với
     * A(1; 2), B(5; 2), C(3; 6)", a model wrote `A = point 1 2` — the only thing the language
     * offered — and the triangle came out four world units across inside axes reaching 320 each
     * way: 1.3% of the axis, so all three vertices and their labels landed on the origin as a
     * single blob. Every line parsed, every object evaluated, nothing was missing, and the
     * figure was unusable. The same mistake put the three x-intercepts of a cubic on top of one
     * another while the curve crossed the axis 40, 109 and −29 units away from them.
     *
     * Kept separate from `point` rather than making `point` scale itself when an `axes` is
     * present. Every plane figure in the corpus places its corners with `point` in world units,
     * and a keyword that means one thing or another depending on a line somewhere else in the
     * script is the kind of quiet disagreement that produces a figure nobody can see is wrong.
     */
    keyword: 'plot',
    parameters: [num('x'), num('y')],
    summary:
      'A point at coordinates read against the axes: `A = plot 1 2` is A(1; 2). ' +
      'Use this — not `point` — whenever the problem gives coordinates or names a point on a ' +
      'graph; `point` is in drawing units, which are a different scale.',
    build: ([x, y]) => ({
      op: 'free-point',
      position: { x: Number(x) * DEFAULT_GRAPH_SCALE, y: Number(y) * DEFAULT_GRAPH_SCALE },
    }),
  },
  {
    keyword: 'midpoint',
    parameters: [ref('a'), ref('b')],
    summary: 'The midpoint of two points.',
    build: ([a, b]) => ({ op: 'midpoint', a, b }),
  },
  {
    keyword: 'divide',
    parameters: [ref('a'), ref('b'), num('of'), num('parts')],
    summary:
      'A point on ab, at `of`/`parts` of the way from a to b. `divide A B 2 3` is AM = (2/3)AB.',
    /*
     * Two whole numbers rather than one decimal, because that is how every problem states it —
     * "AM = 2/3 AB", "trung điểm" being the case that already has its own keyword. Writing
     * `divide A B 0.6667` would be both uglier and lossy.
     *
     * The reading is *of the whole*, not `AM : MB`. A problem phrased the other way — "M chia
     * AB theo tỉ số 2 : 3" — converts by adding: that is `divide A B 2 5`.
     */
    build: ([a, b, of, parts]) => ({
      op: 'ratio-point',
      a,
      b,
      t: Number(of) / Number(parts),
    }),
  },
  {
    keyword: 'intersect',
    parameters: [ref('first'), ref('second')],
    summary: 'Where two curves cross. Use `intersect2` for the second crossing.',
    build: ([a, b]) => ({ op: 'intersection', a, b, index: 0 }),
  },
  {
    keyword: 'intersect2',
    parameters: [ref('first'), ref('second')],
    summary: 'The second crossing of two curves, where there is one.',
    build: ([a, b]) => ({ op: 'intersection', a, b, index: 1 }),
  },
  {
    keyword: 'project',
    parameters: [ref('point'), ref('onto')],
    summary: 'The foot of the perpendicular from a point to a curve.',
    build: ([point, onto]) => ({ op: 'projected-point', point, onto }),
  },
  {
    keyword: 'reflect',
    parameters: [ref('point'), ref('mirror')],
    summary: 'A point reflected in a line, or in another point.',
    build: ([point, mirror]) => ({ op: 'reflected-point', point, mirror }),
  },
  {
    keyword: 'rotate',
    parameters: [ref('point'), ref('centre'), num('degrees')],
    summary: 'A point rotated about a centre.',
    build: ([point, center, degrees]) => ({
      op: 'rotated-point',
      point,
      center,
      angle: ((degrees as number) * Math.PI) / 180,
    }),
  },
  {
    keyword: 'scale',
    parameters: [ref('point'), ref('centre'), num('factor')],
    summary: 'A point scaled about a centre.',
    build: ([point, center, factor]) => ({ op: 'scaled-point', point, center, factor }),
  },

  /* ------------------------------------------------------------------ linear */
  {
    keyword: 'segment',
    parameters: [ref('a'), ref('b')],
    summary: 'A segment between two points.',
    build: ([a, b]) => ({ op: 'segment', a, b }),
  },
  {
    keyword: 'ray',
    parameters: [ref('origin'), ref('through')],
    summary: 'A ray from a point through another.',
    build: ([origin, through]) => ({ op: 'ray', origin, through }),
  },
  {
    keyword: 'line',
    parameters: [ref('a'), ref('b')],
    summary: 'An endless line through two points.',
    build: ([a, b]) => ({ op: 'line', a, b }),
  },
  {
    keyword: 'vector',
    parameters: [ref('from'), ref('to')],
    summary: 'An arrow between two points.',
    build: ([from, to]) => ({ op: 'vector', from, to }),
  },
  {
    keyword: 'parallel',
    parameters: [ref('reference'), ref('through')],
    summary: 'A line parallel to another, through a point.',
    build: ([reference, through]) => ({ op: 'parallel-line', reference, through }),
  },
  {
    keyword: 'perpendicular',
    parameters: [ref('reference'), ref('through')],
    summary: 'A line perpendicular to another, through a point.',
    build: ([reference, through]) => ({ op: 'perpendicular-line', reference, through }),
  },
  {
    keyword: 'bisector',
    parameters: [ref('a'), ref('b')],
    summary: 'The perpendicular bisector of two points.',
    build: ([a, b]) => ({ op: 'perpendicular-bisector', a, b }),
  },
  {
    keyword: 'anglebisector',
    parameters: [ref('a'), ref('vertex'), ref('c')],
    summary: 'The bisector of angle a–vertex–c.',
    build: ([a, vertex, c]) => ({ op: 'angle-bisector', a, vertex, c }),
  },
  {
    keyword: 'tangent',
    parameters: [ref('circle'), ref('from')],
    summary:
      'A tangent to a circle from a point outside it: `tangent c A`. Use `tangent2` for the ' +
      'other one. With a **number** instead of a point it is the tangent to a graph at that ' +
      'abscissa: `tangent C 2` is the tiếp tuyến of (C) at the point where x = 2.',
    build: ([conic, from]) => ({ op: 'tangent-line', conic, from, index: 0 }),
  },
  {
    /*
     * The second tangent, which the kernel could always draw and the language could not name.
     *
     * `tangent-line` has carried an `index` selecting between the two from the beginning; only
     * `index: 0` was ever reachable. The gap showed up the first time a model was asked for
     * "hai tiếp tuyến PA và PB tới đường tròn" — one of the commonest circle problems there is —
     * and it wrote `tangent2` unprompted, by analogy with `intersect2`. It was right about what
     * the language should have been called; it just did not exist.
     */
    keyword: 'tangent2',
    parameters: [ref('circle'), ref('from')],
    summary: 'The other tangent to a circle from a point outside it.',
    build: ([conic, from]) => ({ op: 'tangent-line', conic, from, index: 1 }),
  },

  /* ------------------------------------------------------------------ conics */
  {
    keyword: 'circle',
    parameters: [ref('centre'), ref('through')],
    summary: 'A circle centred on one point, through another.',
    build: ([center, through]) => ({ op: 'circle-through', center, through }),
  },
  {
    keyword: 'circler',
    parameters: [ref('centre'), num('radius')],
    summary: 'A circle of a given radius.',
    build: ([center, radius]) => ({ op: 'circle-radius', center, radius }),
  },
  {
    /*
     * A point on a curve, by angle — the plane counterpart of `rim`.
     *
     * `locus-point` is how the editor's "On Circle" tool has always worked, and it had no name
     * in the written language: a script asked for "lấy điểm A trên đường tròn (O)" had to work
     * out coordinates and place a free point, which is on the circle until anything moves. A
     * model handed the language reached for `rim` instead, which belongs to solids and refused.
     *
     * Kept as a separate word rather than widening `rim`, because zero points somewhere else in
     * each. On a solid the angle is measured in the base plane, where zero faces the viewer; on
     * the page it is measured from the x axis, where zero faces right. One keyword with two
     * zeroes is the kind of quiet disagreement that produces a figure nobody can see is wrong.
     *
     * The parameter is a fraction of a turn rather than degrees, so `on c 450` and `on c 90`
     * name the same point instead of one of them falling off the end.
     */
    keyword: 'on',
    parameters: [ref('curve'), num('degrees')],
    summary:
      'A point on a curve: `A = on c 100`. On a circle the number is an angle anticlockwise ' +
      'from the x axis; on a segment or an arc a full turn means the whole of it, so 180 is ' +
      'halfway along.',
    build: ([host, degrees]) => ({ op: 'locus-point', host, t: Number(degrees) / 360 }),
  },
  {
    keyword: 'circle3',
    parameters: [ref('a'), ref('b'), ref('c')],
    summary: 'The circle through three points.',
    build: ([a, b, c]) => ({ op: 'circle-3-points', a, b, c }),
  },
  {
    keyword: 'arc3',
    parameters: [ref('start'), ref('through'), ref('end')],
    summary: 'An arc through three points.',
    build: ([a, b, c]) => ({ op: 'arc-3-points', a, b, c }),
  },
  {
    keyword: 'ellipse',
    parameters: [ref('focus1'), ref('focus2'), ref('through')],
    summary: 'An ellipse from its two foci and a point on it.',
    build: ([f1, f2, through]) => ({ op: 'ellipse-foci', f1, f2, through }),
  },

  /* --------------------------------------------------------- triangle centres */
  {
    keyword: 'circumcenter',
    parameters: [ref('a'), ref('b'), ref('c')],
    summary: 'The centre of the circle through three points.',
    build: ([a, b, c]) => ({ op: 'circumcenter', a, b, c }),
  },
  {
    keyword: 'incenter',
    parameters: [ref('a'), ref('b'), ref('c')],
    summary: 'The centre of the inscribed circle.',
    build: ([a, b, c]) => ({ op: 'incenter', a, b, c }),
  },
  {
    keyword: 'centroid',
    parameters: [ref('a'), ref('b'), ref('c')],
    summary: 'Where the medians meet.',
    build: ([a, b, c]) => ({ op: 'centroid', a, b, c }),
  },
  {
    keyword: 'orthocenter',
    parameters: [ref('a'), ref('b'), ref('c')],
    summary: 'Where the altitudes meet.',
    build: ([a, b, c]) => ({ op: 'orthocenter', a, b, c }),
  },

  /* -------------------------------------------------------------- annotation */
  /*
   * The statements a *solid* figure needs, and a plane one mostly does not.
   *
   * Writing the cone problem in this language was the test that found them missing. Everything
   * above builds objects out of other objects, which is exactly right for plane geometry —
   * where a figure is a construction — and not enough for a drawing of a solid, where the
   * outline, the hidden edges and the base rim are *drawn* rather than derived. A projection
   * has no constructive definition inside the plane it is drawn on.
   *
   * So: an ellipse given its own radii, a free-standing label, an angle mark, and a way to say
   * that a line is hidden. With those a cone or a prism can be written; without them the
   * language describes half a figure and stops.
   *
   * There is no `polyline`. A run of segments through *named* points is what `segment` already
   * does one at a time, and `free-polyline` holds raw coordinates rather than references — a
   * statement taking forty numbers is not one a teacher would ever type.
   */
  {
    keyword: 'oval',
    parameters: [num('x'), num('y'), num('rx'), num('ry')],
    summary:
      'An ellipse drawn at a place, from its own radii: `oval 0 0 180 55`. ' +
      'This is how the base of a solid is drawn — a circle seen at an angle.',
    /*
     * Coordinates rather than a centre point, and the difference is not cosmetic.
     *
     * Everything else in this language *constructs*: it names other objects and derives a new
     * one, so moving a parent moves the child. A free ellipse holds its own centre, so there
     * is nothing to derive it from — the first attempt took a reference, compiled, parsed, and
     * evaluated to nothing, because an entity id had been written where a coordinate belongs.
     */
    build: ([x, y, rx, ry]) => ({ op: 'free-ellipse', center: { x, y }, rx, ry, rotation: 0 }),
  },
  {
    keyword: 'text',
    parameters: [ref('at'), str('content')],
    summary:
      'A label standing on its own, at a point: `text P "60°"`. ' +
      'Unlike `label`, it names nothing — use it for a quantity such as a length or an angle.',
    build: ([at, content]) => ({
      op: 'text',
      anchor: at,
      offset: { x: 0, y: 0 },
      content,
      latex: false,
    }),
  },
  {
    keyword: 'anglemark',
    parameters: [ref('a'), ref('vertex'), ref('c')],
    summary: 'An arc marking the angle at `vertex`: `anglemark A B C`.',
    build: ([a, vertex, c]) => ({ op: 'angle-mark', a, vertex, c, ticks: 1, showValue: false }),
  },
  {
    keyword: 'anglemarkbig',
    parameters: [ref('a'), ref('vertex'), ref('c')],
    summary:
      'A larger arc, for a figure in projection: `anglemarkbig A B C`. ' +
      'Foreshortening closes an angle up until an ordinary arc leaves no room for its label.',
    build: ([a, vertex, c]) => ({
      op: 'angle-mark',
      a,
      vertex,
      c,
      ticks: 1,
      size: 1.8,
      showValue: false,
    }),
  },
  {
    keyword: 'anglevalue',
    parameters: [ref('a'), ref('vertex'), ref('c')],
    summary:
      'An angle mark that writes its own measure: `anglevalue A B C`. ' +
      'For plane figures only — in a projection the measured angle is not the real one.',
    build: ([a, vertex, c]) => ({ op: 'angle-mark', a, vertex, c, ticks: 1, showValue: true }),
  },

  /* ------------------------------------------------------------------ graphs */
  /*
   * The two statements a problem about a *function* needs, and the reason they exist.
   *
   * Before them the language had `segment` and `line` and nothing else, so a model asked for
   * "đồ thị hàm số f(x) liên tục trên [−1;5]" could only join a handful of points with straight
   * lines and draw the axes as two infinite lines. What came back was a sawtooth between two
   * dashed rules — every part of it true to the script and none of it a graph.
   *
   * They build the **same entities the toolbar builds**, which is the point rather than a
   * detail: what arrives on the canvas is a real graph and a real pair of axes, so the formula
   * can be edited in the panel, the range dragged, and a variation table taken from it. A
   * figure that can only be looked at is worth much less to a teacher than one they can adjust.
   */
  {
    keyword: 'axes',
    parameters: [num('extent')],
    summary:
      'A numbered pair of coordinate axes reaching `extent` units each way: `axes 10`. ' +
      'Draw one before any `graph`, and give it at least the range the graph covers.',
    build: ([extent]) => ({
      op: 'axes',
      scale: DEFAULT_GRAPH_SCALE,
      extent,
      step: 1,
      numbered: true,
    }),
  },
  {
    keyword: 'graph',
    parameters: [str('formula'), num('from'), num('to')],
    summary:
      'The curve of `y = f(x)` over a range: `graph "x^3-3x" -3 3`. ' +
      'Written as it would be on a blackboard — `x^2`, `sqrt(x)`, `1/x`, `sin(x)`, `abs(x)`.',
    build: ([formula, from, to]) => ({
      op: 'function-graph',
      expression: formula,
      from,
      to,
      scale: DEFAULT_GRAPH_SCALE,
    }),
  },

  /* ------------------------------------------------------------------ planes */
  /*
   * The three statements a solid-geometry problem is actually written in.
   *
   * Before these, a plane was a *style name*: a section was drawn by typing its answer as a
   * polygon, which only works for someone who has already solved the problem and is checked by
   * nothing. A teacher names a plane, asks for its section, and asks where two planes meet —
   * and the last two are now answered by the program rather than by the person typing.
   *
   * The solid is not written down. It is found from the points: `plane S A C` names three
   * corners of one figure, and asking which figure would be asking the author to repeat
   * themselves. Points from two different solids are the error, and it is reported as one.
   */
  {
    keyword: 'plane',
    parameters: [ref('a'), ref('b'), ref('c')],
    summary:
      'A plane through three of the figure’s points, drawn as its section with the solid: ' +
      '`p = plane S A C`. The section is *computed*, so it stays right when the solid turns.',
    build: ([a, b, c]) => ({ op: 'plane', solid: a, a, b, c }),
  },
  {
    keyword: 'planepar',
    parameters: [ref('through'), ref('to')],
    summary:
      'A plane through a point, parallel to a plane already named: `q = planepar M p`. ' +
      '“Mặt phẳng qua M song song với (ABC)”.',
    build: ([through, to]) => ({ op: 'plane-parallel', solid: through, through, to }),
  },
  /**
   * The one thing a paper says about a *pair* of planes, and the only plane statement that
   * describes an angle rather than a position.
   *
   * `turn`, not `plane60` or `planeangle`: it is a rotation, and saying so is what makes the
   * answer single. "A plane at 60° to that one" names a cone of planes; "that plane, turned 60°
   * about AB" names one. The direction `a`→`b` carries the sign, so writing the two points the
   * other way round is how the other of the two solutions is asked for — and `-60` says the same
   * thing, for an author who would rather type a number than reorder a line.
   */
  {
    keyword: 'turn',
    parameters: [ref('from'), ref('a'), ref('b'), num('degrees')],
    summary:
      'A plane turned about a line in it, by a stated angle: `q = turn p A B 60`. ' +
      '“Mặt phẳng qua AB hợp với (ABCD) một góc 60°”. `A B` the other way round turns it the ' +
      'other way, as does a negative angle.',
    build: ([from, a, b, degrees]) => ({
      op: 'plane-turn',
      solid: from,
      from,
      a,
      b,
      degrees,
    }),
  },
  /**
   * A plane with nothing to cut — the one a teacher draws on a blackboard.
   *
   * `plane3` rather than `plane`, because they are two objects and not two spellings of one:
   * `plane` is a **section** whose shape the solid decides, and this is a parallelogram standing
   * for a surface, whose size the author decides. Naming them alike would invite an author to
   * expect a section from a figure that has no body.
   *
   * The viewpoint is not written down. The first `plane3` brings one, as `graph` brings its axes,
   * and every later plane shares it — which is what stops two planes in one figure from being
   * drawn through different projections and quietly making a false picture.
   */
  {
    keyword: 'plane3',
    parameters: [num('tilt')],
    summary:
      'A plane on its own, drawn as a leaning parallelogram: `p = plane3 0`, `q = plane3 60`. ' +
      'Two of them differing in tilt meet at that angle, and the giao tuyến is computed.',
    build: ([tilt]) => ({ op: 'plane-free', space: PENDING_SPACE, tilt, size: 200 }),
  },
  {
    keyword: 'onplane',
    parameters: [ref('plane'), num('u'), num('v')],
    summary:
      'A point lying in a plane, at a position in that plane’s own axes: `M = onplane p 60 -40`. ' +
      'Join two of them with `segment` to draw a line **in** the plane.',
    build: ([plane, u, v]) => ({ op: 'plane-point', plane, u, v }),
  },

  /**
   * The dihedral, marked where it is rather than where it looks like it is.
   *
   * Two planes and nothing else: the giao tuyến, the point on it, and the two perpendiculars are
   * all worked out. Marking it by hand needs `anglemark S M O` — right only for an author who has
   * already done that construction, which is the step the problem is usually asking about.
   */
  {
    keyword: 'dihedral',
    parameters: [ref('a'), ref('b')],
    summary:
      'The angle between two planes, marked at the giao tuyến: `d = dihedral p q`. ' +
      'The arc goes where a book puts it — two rays perpendicular to the line where they meet.',
    build: ([a, b]) => ({
      op: 'dihedral-mark',
      solid: a,
      a,
      b,
      ticks: 1,
      showValue: false,
    }),
  },
  {
    keyword: 'meet',
    parameters: [ref('a'), ref('b')],
    summary:
      'The giao tuyến of two planes, as the segment of it inside the solid: `d = meet p q`. ' +
      '`(SAC)` and `(SBD)` meet along the height of the pyramid, and this works that out.',
    build: ([a, b]) => ({ op: 'plane-meet', solid: a, a, b }),
  },

  /* ----------------------------------------------------------------- regions */
  {
    keyword: 'polygon',
    parameters: [],
    summary: 'A polygon through any number of points: `polygon A B C D`.',
    build: (vertices) => ({ op: 'polygon', vertices }),
  },

  /* ------------------------------------------------------------- measurement */
  {
    keyword: 'measure',
    parameters: [str('what')],
    summary:
      'A live measurement of the objects that follow: ' +
      '`measure "angle" A B C`, `measure "length" AB`.',
    build: ([what, ...targets]) => ({
      op: 'measurement',
      measure: what,
      targets,
      offset: { x: 0, y: 16 },
    }),
  },
];

/**
 * What `marks` can be asked to point out on a curve.
 *
 * Spelled as a teacher would say it rather than as the domain names it: a lesson asks for
 * `cực trị` and `giao điểm với trục hoành`, and the two turning kinds are one idea to the
 * person writing the worksheet even though the code has to tell a maximum from a minimum.
 *
 * `marks C` with nothing after it means all of them.
 */
export const MARK_GROUPS: Record<string, readonly NotableKind[]> = {
  extrema: [NotableKind.Maximum, NotableKind.Minimum],
  roots: [NotableKind.Root],
  intercept: [NotableKind.Intercept],
};

/** Statements taking a variable number of trailing references. */
export const VARIADIC = new Set(['polygon', 'measure']);

/** The measure names `measure` accepts, for validation and for the help text. */
export const MEASURES: readonly string[] = [
  'length',
  'distance',
  'angle',
  'area',
  'perimeter',
  'radius',
  'diameter',
  'coordinates',
  'slope',
  'vector-components',
];

const BY_KEYWORD = new Map(STATEMENTS.map((statement) => [statement.keyword, statement]));

export function lookupStatement(keyword: string): StatementSpec | undefined {
  return BY_KEYWORD.get(keyword);
}
